/**
 * Canvas scale: pixels map to real-world metres.
 * 200px = 1m → 1 grid square (10px) = 5cm.
 */
export const PIXELS_PER_METRE = 200;
export const GRID_SPACING = 10;

/** Default wall thickness in pixels (~0.09 m at 200 px/m). */
export const DEFAULT_WALL_THICKNESS = 18;

/** Line weights for the wall tool. Cabinets and counters are the same stroke, drawn lighter. */
export const WALL_LINE_PRESETS = [
  { id: 'wall', label: 'Wall', description: 'Standard wall', thickness: DEFAULT_WALL_THICKNESS },
  { id: 'cabinet', label: 'Cabinet', description: 'Bedroom cabinets', thickness: 8 },
  { id: 'counter', label: 'Counter', description: 'Kitchen countertop', thickness: 3 },
] as const;

export type WallLinePresetId = (typeof WALL_LINE_PRESETS)[number]['id'];

export function nearestWallPreset(thickness: number): (typeof WALL_LINE_PRESETS)[number] {
  return WALL_LINE_PRESETS.reduce((best, preset) =>
    Math.abs(preset.thickness - thickness) < Math.abs(best.thickness - thickness) ? preset : best
  );
}

/** Room name and dimension type, sized so it stays readable on a typical plan. */
export function roomTextLayout(wPx: number, hPx: number): {
  labelSize: number;
  dimSize: number;
  showDims: boolean;
  labelY: number;
  dimY: number;
} {
  const short = Math.min(Math.max(wPx, 0), Math.max(hPx, 0));
  const labelSize = Math.round(Math.min(28, Math.max(18, short * 0.11)));
  const dimSize = Math.round(Math.min(18, Math.max(14, labelSize * 0.72)));
  const showDims = wPx >= 150 && hPx >= 120;
  const gap = 6;
  const dimBlock = showDims ? dimSize * 2.15 : 0;
  const block = labelSize + (showDims ? gap + dimBlock : 0);
  const labelY = Math.max(8, (hPx - block) / 2);
  const dimY = labelY + labelSize + gap;
  return { labelSize, dimSize, showDims, labelY, dimY };
}

/** Snap an angle (radians) to the nearest 45° increment. */
export function snapAngle(p0: { x: number; y: number }, p1: { x: number; y: number }): { x: number; y: number } {
  const dx = p1.x - p0.x;
  const dy = p1.y - p0.y;
  const dist = Math.hypot(dx, dy);
  if (dist === 0) return p1;
  const step = Math.PI / 4;
  const angle = Math.round(Math.atan2(dy, dx) / step) * step;
  return { x: p0.x + Math.cos(angle) * dist, y: p0.y + Math.sin(angle) * dist };
}

/** Convert canvas pixels to metres */
export function pixelsToMetres(px: number): number {
  return px / PIXELS_PER_METRE;
}

/** Convert metres to canvas pixels */
export function metresToPixels(m: number): number {
  return m * PIXELS_PER_METRE;
}

const SQFT_PER_SQM = 10.7639;

/** Square metres → square feet. */
export function sqmToSqft(m2: number): number {
  return m2 * SQFT_PER_SQM;
}

/** Format metres as feet'inches" (UK estate-agent style), e.g. 4.14 → 13'7". */
export function metresToFeetInches(m: number): string {
  const totalInches = m / 0.0254;
  let ft = Math.floor(totalInches / 12);
  let inch = Math.round(totalInches - ft * 12);
  if (inch === 12) {
    ft += 1;
    inch = 0;
  }
  return `${ft}'${inch}"`;
}

/** Imperial + metric dimension lines for a room of the given pixel size. */
export function dimensionStrings(wPx: number, hPx: number): { imperial: string; metric: string } {
  const wM = pixelsToMetres(wPx);
  const hM = pixelsToMetres(hPx);
  return {
    imperial: `${metresToFeetInches(wM)} x ${metresToFeetInches(hM)}`,
    metric: `(${wM.toFixed(1)}m x ${hM.toFixed(1)}m)`,
  };
}

/** Human-readable scale for status bar (e.g. "1 square = 20cm") */
const cmPerSquare = Math.round((GRID_SPACING / PIXELS_PER_METRE) * 100);
export const SCALE_LABEL = cmPerSquare >= 100 ? '1 square = 1m' : `1 square = ${cmPerSquare}cm`;

/** Snap a canvas coordinate to grid (if snap enabled) */
export function snapToGrid(value: number, enabled: boolean): number {
  if (!enabled) return value;
  return Math.round(value / GRID_SPACING) * GRID_SPACING;
}

export function snapPoint(
  p: { x: number; y: number },
  enabled: boolean
): { x: number; y: number } {
  return {
    x: snapToGrid(p.x, enabled),
    y: snapToGrid(p.y, enabled),
  };
}
